/**
 * Minimal visitor-tracking beacon -- the only site-visit signal the backend has (see
 * page_visits migration's own comment; before this, nothing tracked anonymous traffic at all).
 * `visitor_id` is a random id generated once per browser and persisted in localStorage, not tied
 * to login -- an anonymous browsing session still counts, and a later sign-in on the same
 * browser doesn't create a second "visitor". Wired into _app.js on initial load and on every
 * client-side route change.
 */
const STORAGE_KEY = "staVisitorId";

// Exported so other beacon-style trackers (see lib/eventEngagement.js) reuse the same
// per-browser id rather than minting a second, inconsistent one.
export function getVisitorId() {
  if (typeof window === "undefined") return null;
  try {
    let id = window.localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      window.localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    // Private browsing / storage blocked -- tracking simply no-ops rather than throwing.
    return null;
  }
}

// sendBeacon survives the page unloading mid-navigation/mid-scroll (the common case for both
// this file's route-change tracker and lib/eventEngagement.js's impression/view trackers);
// falls back to fetch with keepalive where it isn't available. Exported so every fire-and-forget
// beacon on the site posts the exact same way instead of each reimplementing this fallback.
export function sendBeaconJson(url, payload) {
  if (typeof window === "undefined") return;
  const body = JSON.stringify(payload);

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch {
    // fall through to fetch
  }

  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

export function trackPageVisit(path) {
  if (typeof window === "undefined") return;
  const visitorId = getVisitorId();
  if (!visitorId) return;

  sendBeaconJson("/api/public/analytics/track", { visitor_id: visitorId, path });
}
